import { Prec, StateEffect, StateField, type Extension } from "@codemirror/state";
import { Decoration, EditorView, WidgetType, type DecorationSet } from "@codemirror/view";

/**
 * Where an AI answer will go. Edits elsewhere move it along; an edit inside it means the
 * answer no longer fits, so the target is dropped. `draft` is the answer so far, shown in
 * place of the text while it streams; without one the text is only marked.
 */
export interface AiTarget {
  from: number;
  to: number;
  draft: string | null;
}

export const setAiTarget = StateEffect.define<AiTarget | null>();

class AiDraftWidget extends WidgetType {
  constructor(readonly text: string) {
    super();
  }

  eq(other: AiDraftWidget): boolean {
    return other.text === this.text;
  }

  toDOM(): HTMLElement {
    const draft = document.createElement("span");
    draft.className = "md-ai-draft";
    draft.setAttribute("aria-live", "polite");
    this.updateDOM(draft);
    return draft;
  }

  updateDOM(dom: HTMLElement): boolean {
    const caret = document.createElement("span");
    caret.className = "md-ai-caret";
    caret.setAttribute("aria-hidden", "true");
    caret.textContent = "▌";
    dom.replaceChildren(this.text, caret);
    return true;
  }

  ignoreEvent(): boolean {
    return false;
  }
}

export const aiTargetField = StateField.define<AiTarget | null>({
  create: () => null,
  update(target, transaction) {
    for (const effect of transaction.effects) if (effect.is(setAiTarget)) return effect.value;
    if (!target || !transaction.docChanged) return target;
    let touched = false;
    transaction.changes.iterChangedRanges((from, to) => {
      // An empty target only marks a spot, so text typed there is an edit to it too.
      if (target.from === target.to ? from <= target.from && to >= target.from : from < target.to && to > target.from)
        touched = true;
    });
    if (touched) return null;
    return {
      ...target,
      from: transaction.changes.mapPos(target.from, 1),
      to: Math.max(transaction.changes.mapPos(target.from, 1), transaction.changes.mapPos(target.to, -1)),
    };
  },
  provide: (field) =>
    EditorView.decorations.from(field, (target): DecorationSet => {
      if (!target) return Decoration.none;
      if (target.draft === null)
        return target.from === target.to
          ? Decoration.none
          : Decoration.set([Decoration.mark({ class: "md-ai-target" }).range(target.from, target.to)]);
      const widget = new AiDraftWidget(target.draft);
      return Decoration.set([
        target.from === target.to
          ? Decoration.widget({ widget, side: 1 }).range(target.from)
          : Decoration.replace({ widget }).range(target.from, target.to),
      ]);
    }),
});

/** Above Live Preview's own widgets, so a streaming answer covers the tables and images it replaces. */
export const aiTargetExtension: Extension = Prec.highest(aiTargetField);

/**
 * Where an answer goes below the text ending at `to`: the end of that line, with blank lines
 * around it so it never runs into the paragraphs either side.
 */
export function insertionBelow(doc: string, to: number): { at: number; prefix: string; suffix: string } {
  const end = doc.indexOf("\n", to > 0 && doc[to - 1] === "\n" ? to - 1 : to);
  const at = end === -1 ? doc.length : end;
  const before = doc.slice(0, at);
  const after = doc.slice(at);
  return {
    at,
    prefix: before === "" || before.endsWith("\n\n") ? "" : before.endsWith("\n") ? "\n" : "\n\n",
    suffix: after.startsWith("\n") && !after.startsWith("\n\n") && after.length > 1 ? "\n" : "",
  };
}
